import React, { useEffect } from "react";
import { createUseStyles } from "react-jss";
import { Heading, LinkButton, TextField } from "@livingdesign/react";
import { Trash } from "@livingdesign/icons";
import { BreakdownByTenureStyles } from "./BreakdownByTenure.styles";
import { useSelector } from "react-redux";
import { amountFormat, parseAmountString } from "../../../helpers/commonUtils";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";

const useStyles = createUseStyles(BreakdownByTenureStyles);



const BreakdownByTenure = ({
  loading,
  tenureTableData = [],
  handleRemoveTenureRow,
  handleTenureDataChange,
  showActions = true,
}) => {
  const classes = useStyles();

  // Calculate and send totals to parent component
  useEffect(() => {
    const totalHours = tenureTableData.reduce((sum, entry) => {
      const numberOfHours = parseFloat(entry.numberOfHours) || 0;
      return sum + numberOfHours;
    }, 0);

    const totalAmount = tenureTableData.reduce((sum, entry) => {
      const budgetAmount = parseFloat(entry.budgetAmount) || 0;
      return sum + budgetAmount;
    }, 0);
  }, [tenureTableData]);

  const renderDedicatedStaff = (value, tableMeta) => {
    const rowIndex = tableMeta.rowIndex;
    // Check if this is the total row by looking at the table data
    const isLastRow = rowIndex === tenureTableData.length;

    if (isLastRow) {
      return <strong>{value}</strong>;
    }

    const rowData = tenureTableData[rowIndex];
    return (
      <TextField
        value={rowData?.dedicatedStaff !== undefined ? rowData.dedicatedStaff : 0}
        placeholder="Staff count"
        data-testid={`dedicated-staff-${rowIndex}`}
        disabled={loading}
        textFieldProps={{
          name: "dedicatedStaff",
          min: "0",
          step: "1"
        }}
        onChange={(e) => {
          const newValue = e.target.value;
          // Only allow positive whole numbers
          if (newValue === "" || (Number.isInteger(Number(newValue)) && Number(newValue) > 0)) {
            handleTenureDataChange && handleTenureDataChange(rowIndex, "dedicatedStaff", newValue);
          }
        }}
        onKeyDown={(e) => {
          // Prevent decimal point, negative sign, and 'e' (scientific notation)
          if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
          }
        }}
        type="number"
      />
    );
  }

  const renderHours = (value, tableMeta) => {
    const rowIndex = tableMeta.rowIndex;
    const isLastRow = rowIndex === tenureTableData.length;

    if (isLastRow) {
      return <strong>{value}</strong>;
    }

    const rowData = tenureTableData[rowIndex];
    return (
      <TextField
        value={rowData?.numberOfHours !== undefined ? rowData.numberOfHours : 0}
        placeholder="Hours"
        data-testid={`numberOfHours-${rowIndex}`}
        disabled={loading}
        textFieldProps={{
          name: "numberOfHours",
          min: "0",
          step: "0.1"
        }}
        onChange={(e) => {
          const newValue = e.target.value;
          // Allow empty string or valid positive numbers with up to 2 decimal places
          if (newValue === "" || (/^\d*\.?\d{0,2}$/.test(newValue) && parseFloat(newValue) >= 0)) {
            handleTenureDataChange && handleTenureDataChange(rowIndex, "numberOfHours", newValue);
          }
        }}
        onBlur={(e) => {
          handleTenureDataChange(rowIndex, "numberOfHours", amountFormat(e.target.value));
        }}
        onKeyDown={(e) => {
          // Prevent negative sign and 'e' (scientific notation)
          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
          }
        }}
        type="text"
      />
    );
  }

  const renderCost = (value, tableMeta) => {
    const rowIndex = tableMeta.rowIndex;
    const isLastRow = rowIndex === tenureTableData.length;

    if (isLastRow) {
      return <strong>${value}</strong>;
    }

    const rowData = tenureTableData[rowIndex];
    return (
      <TextField
        value={rowData?.budgetAmount !== undefined ? rowData.budgetAmount : 0}
        placeholder="Cost"
        data-testid={`budgetAmount-${rowIndex}`}
        disabled={loading}
        textFieldProps={{
          name: "budgetAmount",
          min: "0",
          step: "0.01"
        }}
        onChange={(e) => {
          const newValue = e.target.value;
          // Allow empty string or valid positive numbers with up to 2 decimal places
          if (newValue === "" || (/^\d*\.?\d{0,2}$/.test(newValue) && parseFloat(newValue) >= 0)) {
            handleTenureDataChange && handleTenureDataChange(rowIndex, "budgetAmount", newValue);
          }
        }}
        onKeyDown={(e) => {
          // Prevent negative sign and 'e' (scientific notation)
          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
          }
        }}
        onBlur={(e) => {
          handleTenureDataChange(rowIndex, "budgetAmount", amountFormat(e.target.value));
        }}
        type="text"
      />
    );
  }

  const TENURE_TABLE_COLUMNS = [
    {
      name: "tenure",
      label: "Tenure",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        })
      }
    },
    // {
    //   name: "classificationsMapped",
    //   label: "Classifications Mapped",
    //   options: {
    //     filter: false,
    //     sort: false,
    //     setCellHeaderProps: () => ({
    //       style: {
    //         minWidth: '150px',
    //         width: '150px',
    //         maxWidth: '150px',
    //         whiteSpace: 'normal',
    //         wordWrap: 'break-word'
    //       }
    //     }),
    //     setCellProps: () => ({
    //       style: {
    //         minWidth: '150px',
    //         width: '150px',
    //         maxWidth: '150px',
    //         whiteSpace: 'normal',
    //         wordWrap: 'break-word'
    //       }
    //     })
    //   }
    // },
    {
      name: "dedicatedStaff",
      label: "# Dedicated staff",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        customBodyRender: renderDedicatedStaff
      }
    },
    {
      name: "numberOfHours",
      label: "# Hours",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        customBodyRender: renderHours
      }
    },
    {
      name: "budgetAmount",
      label: "$ Cost (USD)",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        customBodyRender: renderCost
      }
    },
    {
      name: "effectiveRate",
      label: "$ Effective Rate",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            whiteSpace: 'normal',
            wordWrap: 'break-word'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const isLastRow = rowIndex === tenureTableData.length;

          if (isLastRow) {
            return <strong>${value}</strong>;
          }

          const rowData = tenureTableData[rowIndex];

          // Calculate effective rate if both numberOfHours and budgetAmount are provided
          const numberOfHours = parseAmountString(rowData?.numberOfHours) || 0;
          const budgetAmount = parseAmountString(rowData?.budgetAmount) || 0;

          let effectiveRate = "";
          if (numberOfHours > 0 && budgetAmount > 0) {
            effectiveRate = (budgetAmount / numberOfHours).toFixed(2);
          }

          return (
            <span data-testid={`effective-rate-${rowIndex}`}>
              {effectiveRate ? `$ ${amountFormat(effectiveRate)}` : "0.00"}
            </span>
          );
        }
      }
    }
  ];

  const TABLE_OPTIONS = {
    selectableRows: "none",
    pagination: false,
    search: false,
    filter: false,
    viewColumns: false,
    download: false,
    print: false,
    responsive: "standard",
    tableBodyHeight: "auto",
    elevation: 0
  };
  // Add action column if showActions is true
  const columns = showActions ? [
    ...TENURE_TABLE_COLUMNS,
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.rowIndex;
          const isLastRow = rowIndex === tenureTableData.length;

          if (isLastRow) {
            return null; // Don't show remove button for total row
          }

          return (
            <LinkButton
              variant="tertiary"
              disabled={loading}
              onClick={() => handleRemoveTenureRow(rowIndex)}
              data-testid="remove-tenure-row"
            >
              <Trash size="small" />
            </LinkButton>
          );
        }
      }
    }
  ] : TENURE_TABLE_COLUMNS;

  // Calculate totals for the total row
  const totalDedicatedStaff = tenureTableData.reduce((sum, entry) => {
    const staff = parseFloat(entry.dedicatedStaff) || 0;
    return sum + staff;
  }, 0);

  const totalHours = tenureTableData.reduce((sum, entry) => {
    const numberOfHours = parseAmountString(entry.numberOfHours) || 0;
    return sum + numberOfHours;
  }, 0);

  const totalAmount = tenureTableData.reduce((sum, entry) => {
    const budgetAmount = parseAmountString(entry.budgetAmount) || 0;
    return sum + budgetAmount;
  }, 0);

  const totalEffectiveRate = totalHours > 0 ? (totalAmount / totalHours).toFixed(2) : "0.00";

  // Create table data with total row
  const tableDataWithTotal = [
    ...tenureTableData,
    {
      tenure: "Total",
      dedicatedStaff: totalDedicatedStaff,
      numberOfHours: totalHours.toFixed(2),
      budgetAmount: totalAmount.toFixed(2),
      effectiveRate: totalEffectiveRate,
      isTotal: true
    }
  ];

  return (
    <div className={classes.estimationTable}>
      <Heading as="h3" size="large" UNSAFE_className={classes.subTitle}>
        Breakdown by Tenure
      </Heading>

      <MuiDataTable
        data={tableDataWithTotal}
        columns={columns}
        customOptions={TABLE_OPTIONS}
        disableSearch={true}
        disableFilter={true}
        disablePagination={true}
        disableExport={true}
        customClassName={`${classes.tenureTable} ${classes.hideScrollbars}`}
      />
    </div>
  );
};

export default BreakdownByTenure;